import React, { SyntheticEvent, ReactElement } from "react";
import cn from "classnames";
import { TrackingObject } from "../../types/TrackingTypes";

import CloseIcon from "../Icons/CloseIcon";
import styles from "./Modal.module.scss";

interface DefaultCloseButtonProps {
    isPayWall: boolean,
    hideModal: () => void,
    closeButtonClassNames: string,
    closeTrackingProps?: TrackingObject
}

/**
 * Generate the X button in the top right of the modal.
 * @param {boolean} props.isPayWall - Is this a paywall and should not be closeable.
 * @param {function} props.closeModal - An additional function to call when the modal is closed in any way.
 * @param {string} props.closeButtonClassNames - Additional styling for the close button.
 * @returns A close button that appears in the top right of the modal.
 */
const DefaultCloseButton = (props: DefaultCloseButtonProps) => {
    const {
        isPayWall,
        hideModal,
        closeButtonClassNames,
        closeTrackingProps
    } = props;
    // if we are a paywall we do not get a close button.
    if (isPayWall) return null;
    return (
        <button
            className={`${styles.closeButton} ${closeButtonClassNames || null}`}
            onClick={() => hideModal()}
            onTouchStart={() => hideModal()}
            type="button"
            aria-label="close dialog"
            data-tracking-props={JSON.stringify(closeTrackingProps)}
            data-tracking-event-type="modal_action"
        >
            <CloseIcon
                data-tracking-props={JSON.stringify(closeTrackingProps)}
                data-tracking-event-type="modal_action"
            />
        </button>
    );
};

interface ModalProps {
    closeButtonClassName?: string;
    containerClassName?: string;
    contentClassName?: string;
    overlayClassName?: string; // deprecated
    timer?: number;
    onClose?: () => void;
    onOpen?: ()  => void;
    setIsOpen: (x: boolean) => void;
    isPayWall?: boolean;
    isOpen?: boolean;
    label?: string;
    impressionTrackingProps?: TrackingObject;
    closeTrackingProps?: Record<string, any>;
}

/**
 * Generates a React Portal modal.
 * @param {string} props.closeButtonClassName - An optional CSS module to override the close button styles.
 * @param {string} props.containerClassName - An optional CSS module to override the container styles.
 * @param {string} props.contentClassName - An optional CSS module to override the content styles.
 * @param {number} props.timer - An optional time in seconds to wait for the modal to auto open.
 * @param {function} props.onClose - An optional external function to call when the modal closes.
 * @param {function} props.onOpen - An optional external function to call when the modal opens.
 * @param {boolean} props.isPayWall - Is this a paywall and therefore not closeable?
 * @param {boolean} props.isOpen - Is the modal currently open. Can be controlled from outside.
 * @param {Function} props.setIsOpen - A function to set the stat of the modal from outside.
 * @param {string} props.label - Announces the modal by name to users with assistive devices
 * @param {React.RefObject<HTMLDialogElement>} props.forwardRef - An optional hook to the modal for external control.
 * @example <Modal {...args}><CONTENT /></Modal>
 * @returns {ReactPortal} An ADA compliant modal as a [React Portal](https://reactjs.org/docs/portals.html).
 */
const Modal = (props: ModalProps & React.HTMLProps<HTMLDialogElement>): ReactElement => {
    const {
        children,
        closeButtonClassName = "",
        containerClassName = "",
        contentClassName = "",
        timer = 0,
        onClose = () => { },
        onOpen = () => { },
        isPayWall = false,
        isOpen = true,
        label = "A basic modal.",
        impressionTrackingProps,
        closeTrackingProps,
        setIsOpen,
        ...restProps
    } = props;

    const [timerMilliseconds, setTimerMilliseconds] = React.useState(timer * 1000);
    const dialog = React.useRef<HTMLDialogElement>(null);
    const closeButtonClassNames = cn(styles.closeButton, closeButtonClassName);
    const containerClassNames = cn(styles.modal, containerClassName);
    const contentClassNames = cn(styles.content, contentClassName);

    const showModal = React.useCallback(() => {
        setIsOpen(true);
        dialog?.current?.showModal();
        dialog?.current?.setAttribute("aria-modal", "true");
        dialog?.current?.setAttribute("aria-hidden", "false");
        document.body.style.overflow = "hidden";
        onOpen();
    }, [onOpen, setIsOpen]);

    const hideModal = React.useCallback(() => {
        setIsOpen(false);
        dialog?.current?.close();
        dialog?.current?.setAttribute("aria-modal", "false");
        dialog?.current?.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "auto";
        onClose();
    }, [onClose, setIsOpen]);

    /**
     * For some undocumented reason the file upload dialog passes a cancel event to all dialogs on the page in chrome.
     */
    const cancelModal = React.useCallback(async (event: SyntheticEvent<HTMLDialogElement, Event>) => {
        hideModal();
        if (event.target instanceof HTMLElement && event.target.nodeName !== "DIALOG") {
            // We need a 10 ms buffer for the dialog to finish closing before reopening.
            setTimeout(showModal, 10);
        }
    }, [hideModal, showModal]);

    const handleBackdropClick = (e: React.MouseEvent) => {
        // We do not allow paywalls to close.
        if (isPayWall) return;
        // If we are the base dialog or by extension its backdrop, Close. Inner elements behave as normal.
        if ((e?.target as HTMLElement)?.nodeName === "DIALOG") {
            hideModal();
        }
    }

    // Internally set the timeout.
    React.useEffect(() => {
        if (timerMilliseconds) {
            setTimeout(() => {
                setTimerMilliseconds(0);
                setIsOpen(true);
            }, timerMilliseconds);
        }

        if (isOpen && !timerMilliseconds) {
            showModal();
        }
        return () => {
            hideModal();
        }
    }, [hideModal, isOpen, setIsOpen, showModal, timerMilliseconds]);

    return (
        <dialog
            aria-atomic="true"
            aria-hidden={true}
            aria-label={label}
            aria-modal={false}
            className={containerClassNames}
            onClick={handleBackdropClick}
            onCancel={cancelModal}
            data-tracking-props={JSON.stringify(closeTrackingProps)}
            data-tracking-event-type="modal_action"
            open={false}
            ref={dialog}
            {...restProps}
        >
            <div className={contentClassNames}>
                {children}
                <DefaultCloseButton {...{ isPayWall, closeButtonClassNames, hideModal, closeTrackingProps }} />
            </div>
        </dialog>
    );
};
Modal.displayName = "Modal";

export default Modal;
export type { ModalProps };
